import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { WebinarOffer } from '@/store/webinar/types';

export interface ValidationError {
  field: string;
  message: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
}

export function useOfferValidation(offer: Partial<WebinarOffer>): ValidationResult {
  useLanguage();
  const errors: ValidationError[] = [];

  // Campos obrigatórios
  if (!offer.title?.trim()) {
    errors.push({ field: 'title', message: translateInterfaceText("Título é obrigatório") });
  }

  if (!offer.cta1_label?.trim()) {
    errors.push({ field: 'cta1_label', message: translateInterfaceText("Texto do botão é obrigatório") });
  }

  if (!offer.cta1_url?.trim()) {
    errors.push({ field: 'cta1_url', message: translateInterfaceText("Link do botão é obrigatório") });
  }

  if (!offer.offer_start_sec || offer.offer_start_sec <= 0) {
    errors.push({ field: 'offer_start_sec', message: translateInterfaceText("Horário de início é obrigatório") });
  }

  // Validação de preços
  if (offer.price_offer && offer.price_original && offer.price_offer > offer.price_original) {
    errors.push({ 
      field: 'price_offer', 
      message: translateInterfaceText("Preço com desconto deve ser menor que o original")
    });
  }

  // Validação de timing
  if (offer.offer_end_sec && offer.offer_start_sec && offer.offer_end_sec <= offer.offer_start_sec) {
    errors.push({ 
      field: 'offer_end_sec', 
      message: translateInterfaceText("Fim da oferta deve ser após o início")
    });
  }

  // Validação de URLs
  const isValidUrl = (url: string): boolean => {
    try {
      new URL(url);
      return url.startsWith('http://') || url.startsWith('https://');
    } catch {
      return false;
    }
  };

  if (offer.cta1_url && !isValidUrl(offer.cta1_url)) {
    errors.push({ field: 'cta1_url', message: translateInterfaceText("URL do botão principal inválida") });
  }

  if (offer.cta2_enabled && offer.cta2_url && !isValidUrl(offer.cta2_url)) {
    errors.push({ field: 'cta2_url', message: translateInterfaceText("URL do segundo botão inválida") });
  }

  // Validação de contraste
  const calculateContrast = (bg: string, fg: string): number => {
    const getLuminance = (hex: string): number => {
      hex = hex.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16) / 255;
      const g = parseInt(hex.substring(2, 4), 16) / 255;
      const b = parseInt(hex.substring(4, 6), 16) / 255;
      
      const sRGB = (c: number) => c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
      return 0.2126 * sRGB(r) + 0.7152 * sRGB(g) + 0.0722 * sRGB(b);
    };

    try {
      const l1 = getLuminance(bg);
      const l2 = getLuminance(fg);
      const lighter = Math.max(l1, l2);
      const darker = Math.min(l1, l2);
      return (lighter + 0.05) / (darker + 0.05);
    } catch {
      return 4.5;
    }
  };

  if (offer.cta1_color) {
    const contrast = calculateContrast(offer.cta1_color, '#ffffff');
    if (contrast < 4.5) {
      errors.push({ 
        field: 'cta1_color', 
        message: translateInterfaceText("Contraste insuficiente: {value0}:1 (mínimo 4.5:1 para acessibilidade)", { value0: (contrast.toFixed(2)) })
      });
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}
